import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api, { BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Save, ArrowLeft, Plus, X, Upload, ImagePlus, Trash2 } from "lucide-react";

const createEmptyProduct = () => ({
  name: "",
  short_description: "",
  full_description: "",
  price: 0,
  tebex_package_id: null,
  image_url: "",
  gallery_images: [],
  features: [],
  video_url: "",
  compatibility: ["QBCore", "ESX", "Standalone"],
  whats_included: [
    "FiveM Asset Escrow protected files",
    "Open configuration files",
    "Installation documentation",
    "Discord support",
  ],
  requirements: ["FiveM server", "Lua 5.4 (lua54)"],
  category: "",
  is_featured: false,
  is_visible: true,
  order: 0,
});

const ListEditor = ({ label, helper, items = [], onChange, placeholder, testId }) => {
  const [draft, setDraft] = useState("");

  const addItem = () => {
    const value = draft.trim();
    if (!value || items.includes(value)) return;
    onChange([...items, value]);
    setDraft("");
  };

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/45 p-4">
      <Label className="text-xs uppercase tracking-widest text-slate-300">{label}</Label>
      <p className="mt-1 min-h-8 text-xs leading-5 text-muted-foreground">{helper}</p>
      <div className="mt-3 flex gap-2">
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addItem();
            }
          }}
          placeholder={placeholder}
          data-testid={`${testId}-input`}
          className="h-10 rounded-lg border-slate-700 bg-slate-950"
        />
        <Button
          type="button"
          onClick={addItem}
          variant="outline"
          aria-label={`Add ${label}`}
          className="h-10 rounded-lg border-primary/30 text-primary hover:bg-primary/10"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <div className="mt-3 flex min-h-10 flex-wrap gap-2" data-testid={testId}>
        {items.map((item, index) => (
          <span
            key={`${item}-${index}`}
            className="inline-flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-slate-200"
          >
            {item}
            <button
              type="button"
              onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))}
              aria-label={`Remove ${item}`}
              className="text-slate-500 transition-colors hover:text-red-300"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
        {items.length === 0 && (
          <span className="py-2 text-xs text-slate-600">Nothing added yet</span>
        )}
      </div>
    </div>
  );
};

const AdminProductEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newFeature, setNewFeature] = useState("");
  const [newGalleryUrl, setNewGalleryUrl] = useState("");
  const [uploadingGallery, setUploadingGallery] = useState(false);

  const [formData, setFormData] = useState(createEmptyProduct);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const catRes = await api.get("/categories");
        setCategories(catRes.data);

        // Fetch product if editing
        if (isEdit) {
          const prodRes = await api.get(`/admin/products/${id}`);
          setFormData({
            ...createEmptyProduct(),
            ...prodRes.data,
            gallery_images: prodRes.data.gallery_images || [],
            features: prodRes.data.features || [],
            compatibility: prodRes.data.compatibility || [],
            whats_included: prodRes.data.whats_included || [],
            requirements: prodRes.data.requirements || [],
          });
        }
      } catch (error) {
        if (isEdit) {
          toast.error("Failed to load product");
          navigate("/admin/products");
        }
      }
      setLoading(false);
    };

    fetchData();
  }, [id, isEdit, navigate]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    let nextValue = value;
    if (type === "number") {
      nextValue = value === "" && name === "tebex_package_id"
        ? null
        : Number(value);
    }
    setFormData({
      ...formData,
      [name]: nextValue
    });
  };

  const handleSwitchChange = (name, checked) => {
    setFormData({ ...formData, [name]: checked });
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()]
      });
      setNewFeature("");
    }
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const uploadImage = async (file) => {
    const uploadData = new FormData();
    uploadData.append("file", file);
    const response = await api.post("/admin/upload", uploadData);
    return `${BACKEND_URL}${response.data.url}`;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const imageUrl = await uploadImage(file);
      setFormData((current) => ({ ...current, image_url: imageUrl }));
      toast.success("Image uploaded");
    } catch (error) {
      toast.error("Failed to upload image");
    }
    e.target.value = "";
  };

  const handleGalleryUpload = async (event) => {
    const availableSlots = Math.max(0, 6 - formData.gallery_images.length);
    const files = Array.from(event.target.files || []).slice(0, availableSlots);
    event.target.value = "";
    if (availableSlots === 0) {
      toast.error("The gallery can contain up to 6 images");
      return;
    }
    if (files.length === 0) return;

    setUploadingGallery(true);
    const uploadedImages = [];
    for (const file of files) {
      try {
        uploadedImages.push(await uploadImage(file));
      } catch (error) {
        toast.error(`Could not upload ${file.name}`);
      }
    }
    if (uploadedImages.length > 0) {
      setFormData((current) => ({
        ...current,
        gallery_images: [...current.gallery_images, ...uploadedImages].slice(0, 6),
      }));
      toast.success(`${uploadedImages.length} gallery image${uploadedImages.length === 1 ? "" : "s"} uploaded`);
    }
    setUploadingGallery(false);
  };

  const addGalleryUrl = () => {
    const imageUrl = newGalleryUrl.trim();
    if (!imageUrl || formData.gallery_images.length >= 6) return;
    setFormData((current) => ({
      ...current,
      gallery_images: [...current.gallery_images, imageUrl],
    }));
    setNewGalleryUrl("");
  };

  const removeGalleryImage = (index) => {
    setFormData((current) => ({
      ...current,
      gallery_images: current.gallery_images.filter((_, imageIndex) => imageIndex !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit) {
        await api.put(`/admin/products/${id}`, formData);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", formData);
        toast.success("Product created");
      }
      navigate("/admin/products");
    } catch (error) {
      toast.error(isEdit ? "Update failed" : "Create failed");
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <AdminLayout title={isEdit ? "Edit Product" : "New Product"}>
        <div className="h-96 flex items-center justify-center">
          <div className="loading-spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEdit ? "Edit Product" : "New Product"}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/admin/products")}
          className="mb-6 rounded-lg"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Products
        </Button>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="product-form">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 space-y-4">
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
                  Basic Information
                </h3>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Product Name
                  </Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="product-name"
                    className="mt-2 bg-slate-950/50 border-slate-800 rounded-lg"
                    placeholder="e.g. AeroCore Helicopter System"
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Short Description
                  </Label>
                  <Input
                    name="short_description"
                    value={formData.short_description}
                    onChange={handleChange}
                    required
                    data-testid="product-short-desc"
                    className="mt-2 bg-slate-950/50 border-slate-800 rounded-lg"
                    placeholder="Brief product description"
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Full Description
                  </Label>
                  <Textarea
                    name="full_description"
                    value={formData.full_description}
                    onChange={handleChange}
                    required
                    data-testid="product-full-desc"
                    className="mt-2 bg-slate-950/50 border-slate-800 rounded-lg min-h-[150px]"
                    placeholder="Detailed product description..."
                  />
                </div>
              </div>

              {/* Features */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
                  Features
                </h3>

                <div className="flex gap-2 mb-4">
                  <Input
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    placeholder="Add new feature..."
                    className="bg-slate-950/50 border-slate-800 rounded-lg"
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
                  />
                  <Button
                    type="button"
                    onClick={addFeature}
                    variant="outline"
                    className="rounded-lg border-slate-700"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-2">
                  {formData.features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-slate-950/50 border border-slate-800 rounded-lg"
                    >
                      <span className="text-sm">{feature}</span>
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {formData.features.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No features added yet
                    </p>
                  )}
                </div>
              </div>

              {/* Technical Details */}
              <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-6">
                <div className="mb-5">
                  <h3 className="text-sm uppercase tracking-widest text-muted-foreground">
                    Technical Details
                  </h3>
                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    These lists appear below the product features so buyers can check compatibility before purchase.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                  <ListEditor
                    label="Compatibility"
                    helper="Frameworks or operating modes supported by this product."
                    items={formData.compatibility}
                    onChange={(compatibility) => setFormData((current) => ({ ...current, compatibility }))}
                    placeholder="e.g. QBCore"
                    testId="product-compatibility"
                  />
                  <ListEditor
                    label="What's Included"
                    helper="Files, documentation and support supplied with the purchase."
                    items={formData.whats_included}
                    onChange={(whats_included) => setFormData((current) => ({ ...current, whats_included }))}
                    placeholder="e.g. Open config files"
                    testId="product-whats-included"
                  />
                  <ListEditor
                    label="Requirements"
                    helper="Server or runtime requirements the customer must have."
                    items={formData.requirements}
                    onChange={(requirements) => setFormData((current) => ({ ...current, requirements }))}
                    placeholder="e.g. lua54"
                    testId="product-requirements"
                  />
                </div>
              </div>

              {/* Media */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 space-y-4">
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
                  Media
                </h3>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Main Image
                  </Label>
                  <div className="mt-2 flex gap-4 items-start">
                    <div className="flex-1">
                      <Input
                        name="image_url"
                        value={formData.image_url}
                        onChange={handleChange}
                        data-testid="product-image-url"
                        className="bg-slate-950/50 border-slate-800 rounded-lg"
                        placeholder="Image URL or upload"
                      />
                    </div>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <div className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 transition-colors flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        <span className="text-sm">Upload</span>
                      </div>
                    </label>
                  </div>
                  {formData.image_url && (
                    <div className="mt-4 w-32 h-32 bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                      <img
                        src={formData.image_url}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>

                <div className="border-t border-slate-800 pt-5">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                        Gallery Images
                      </Label>
                      <p className="mt-1 text-xs text-slate-500">
                        Add 4–6 screenshots. Buyers can switch the main image or open it full screen.
                      </p>
                    </div>
                    <span className="text-xs font-semibold text-primary">
                      {formData.gallery_images.length}/6
                    </span>
                  </div>

                  <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                    <Input
                      value={newGalleryUrl}
                      onChange={(event) => setNewGalleryUrl(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          addGalleryUrl();
                        }
                      }}
                      disabled={formData.gallery_images.length >= 6}
                      data-testid="product-gallery-url"
                      className="h-10 flex-1 rounded-lg border-slate-800 bg-slate-950/50"
                      placeholder="Paste an image URL or upload files"
                    />
                    <Button
                      type="button"
                      onClick={addGalleryUrl}
                      disabled={!newGalleryUrl.trim() || formData.gallery_images.length >= 6}
                      variant="outline"
                      className="h-10 rounded-lg border-slate-700"
                    >
                      <Plus className="h-4 w-4" /> Add URL
                    </Button>
                    <label
                      className={`inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-4 text-sm text-primary transition-colors hover:bg-primary/15 ${
                        formData.gallery_images.length >= 6 || uploadingGallery
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }`}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleGalleryUpload}
                        className="hidden"
                      />
                      <ImagePlus className="h-4 w-4" />
                      {uploadingGallery ? "Uploading…" : "Upload images"}
                    </label>
                  </div>

                  {formData.gallery_images.length > 0 ? (
                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {formData.gallery_images.map((image, index) => (
                        <div
                          key={`${image}-${index}`}
                          className="group relative aspect-video overflow-hidden rounded-lg border border-slate-800 bg-slate-950"
                        >
                          <img src={image} alt={`Gallery preview ${index + 1}`} className="h-full w-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeGalleryImage(index)}
                            aria-label={`Remove gallery image ${index + 1}`}
                            className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-md border border-red-400/30 bg-slate-950/90 text-red-300 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-4 rounded-lg border border-dashed border-slate-800 px-4 py-8 text-center text-sm text-slate-600">
                      No gallery screenshots added yet
                    </div>
                  )}
                </div>

                <div className="border-t border-slate-800 pt-5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    YouTube Demo URL (Optional)
                  </Label>
                  <Input
                    name="video_url"
                    value={formData.video_url}
                    onChange={handleChange}
                    data-testid="product-video-url"
                    className="mt-2 bg-slate-950/50 border-slate-800 rounded-lg"
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    A normal YouTube or youtu.be link is accepted and converted to a privacy-friendly embed.
                  </p>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Pricing */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 space-y-4">
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
                  Price & Purchase
                </h3>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Price (USD)
                  </Label>
                  <Input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    step="0.01"
                    min="0"
                    data-testid="product-price"
                    className="admin-number-input mt-2 bg-slate-950/50 border-slate-800 rounded-lg"
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Tebex Package ID
                  </Label>
                  <Input
                    type="number"
                    name="tebex_package_id"
                    value={formData.tebex_package_id ?? ""}
                    onChange={handleChange}
                    min="1"
                    step="1"
                    data-testid="product-tebex-package-id"
                    className="admin-number-input mt-2 bg-slate-950/50 border-slate-800 rounded-lg"
                    placeholder="Tebex package number"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Copy the numeric package ID from your Tebex creator panel. Products without an ID cannot be purchased.
                  </p>
                </div>
              </div>

              {/* Category */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
                  Category
                </h3>
                <Select
                  value={formData.category || "none"}
                  onValueChange={(category) => setFormData((current) => ({
                    ...current,
                    category: category === "none" ? "" : category,
                  }))}
                >
                  <SelectTrigger
                    data-testid="product-category"
                    className="h-11 rounded-lg border-slate-700 bg-slate-950/80 text-foreground focus:ring-primary"
                  >
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-950 text-slate-100">
                    <SelectItem value="none" className="focus:bg-primary/15 focus:text-primary">
                      Select category
                    </SelectItem>
                    {categories.map((cat) => (
                      <SelectItem
                        key={cat.id}
                        value={cat.slug}
                        className="focus:bg-primary/15 focus:text-primary"
                      >
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 space-y-4">
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
                  Status
                </h3>

                <div className="flex items-center justify-between">
                  <Label className="text-sm">Visible</Label>
                  <Switch
                    checked={formData.is_visible}
                    onCheckedChange={(checked) => handleSwitchChange("is_visible", checked)}
                    data-testid="product-visible"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label className="text-sm">Featured</Label>
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => handleSwitchChange("is_featured", checked)}
                    data-testid="product-featured"
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Sort Order
                  </Label>
                  <Input
                    type="number"
                    name="order"
                    value={formData.order}
                    onChange={handleChange}
                    min="0"
                    data-testid="product-order"
                    className="admin-number-input mt-2 bg-slate-950/50 border-slate-800 rounded-lg"
                  />
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={saving}
                data-testid="save-product-btn"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg uppercase tracking-widest font-bold py-6 glow-blue-hover"
              >
                {saving ? (
                  <span className="loading-spinner w-5 h-5" />
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    {isEdit ? "Update" : "Create"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </motion.div>
    </AdminLayout>
  );
};

export default AdminProductEdit;
